/**
 * Gate writes: every one becomes a named command in the outbox, plus the phone's own view
 * of the day (session record, drafts). Payloads are validated by the schemas in ../commands.
 */
import {
  clockSkewSeconds,
  enqueueCommand,
  enqueueCommands,
  enqueueFromDraft,
  getDb,
  getMeta,
  nextDailyCounter,
  openOutboxRecords,
  roster,
  sealValue,
  saveDraft,
  setMeta,
  type EnqueueInput,
} from '@/offline'
import { newId } from '@/lib/ids'
import { saveLastFix } from '@/lib/location'
import type { SupplyChainDB } from '@/offline/db'
import {
  DEVICE_PREFIX_RE,
  sessionCode,
  VILLAGE_CODE_RE,
  villageCodeFromQrPayloads,
} from '@/lib/codes'
import '../commands'
import { scheduleDraftBackup } from './draftBackup'
import type { AddBankAccountPayload } from '../commands'
import type {
  CloseSessionPayload,
  ConfirmRollPayload,
  OpenSessionPayload,
  RetagPayload,
  SpoilSlipPayload,
  VoidDeliveryReason,
  VoidTagPayload,
} from '../commands'
import {
  buildCommitPayload,
  buildVoidPayload,
  deliveryReducer,
  DRAFT_KIND,
  type CardRef,
  type DeliveryDraft,
} from '../model/delivery'
import type { SeasonLight } from '../model/seasonLight'
import { lotCode } from '../model/codes'
import { getSession, saveSession, type ConfirmedRoll, type GateSession } from './session'

/** Best-effort position for open_session; never blocks the day. */
export function currentGps(timeoutMs = 4000): Promise<OpenSessionPayload['gps']> {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) return resolve(null)
    const timer = setTimeout(() => resolve(null), timeoutMs + 500)
    navigator.geolocation.getCurrentPosition(
      (p) => {
        clearTimeout(timer)
        // The gate does not move: this fix is the fallback when GPS cannot lock later.
        saveLastFix({
          lat: p.coords.latitude,
          lng: p.coords.longitude,
          accuracy_m: p.coords.accuracy,
          captured_at: new Date(p.timestamp || Date.now()).toISOString(),
        })
        resolve({
          lat: p.coords.latitude,
          lng: p.coords.longitude,
          accuracy_m: p.coords.accuracy ?? null,
        })
      },
      () => {
        clearTimeout(timer)
        resolve(null)
      },
      { enableHighAccuracy: false, timeout: timeoutMs, maximumAge: 600_000 },
    )
  })
}

export async function mintSessionCode(
  at: Date | number = Date.now(),
  db: SupplyChainDB = getDb(),
): Promise<string> {
  const device = await getMeta('device', db)
  if (!device) throw new Error('This phone is not registered')
  const rolls = await roster.rolls(db)
  const rollVillage = villageCodeFromQrPayloads(
    rolls.flatMap((roll) => roll.payloads?.slice(0, 1) ?? []),
  )
  // Issued rolls are authoritative for the village and remain available offline. This repairs
  // registrations cached before qr_villages was created, which used to mint `CS-date--prefix-nn`.
  const villageCode = rollVillage ?? device.team.code.trim().toUpperCase()
  const devicePrefix = device.code_prefix.trim().toUpperCase()
  if (!VILLAGE_CODE_RE.test(villageCode)) {
    throw new Error(
      'The village code is missing. Connect once and tap More → Update farmer list, then open the day.',
    )
  }
  if (!DEVICE_PREFIX_RE.test(devicePrefix)) {
    throw new Error('This phone has an invalid code prefix. Ask the office to update the phone.')
  }
  if (villageCode !== device.team.code || devicePrefix !== device.code_prefix) {
    await setMeta(
      'device',
      {
        ...device,
        code_prefix: devicePrefix,
        team: { ...device.team, code: villageCode },
      },
      db,
    )
  }
  const counter = await nextDailyCounter('session', at, db)
  return sessionCode({
    villageCode,
    devicePrefix,
    counter,
    at,
  })
}

export async function mintLot(
  at: Date | number | string = Date.now(),
): Promise<{ id: string; code: string }> {
  const device = await getMeta('device')
  if (!device) throw new Error('This phone is not registered')
  const when = typeof at === 'string' ? Date.parse(at) : at
  const counter = await nextDailyCounter('lot', when)
  return { id: newId(), code: lotCode({ devicePrefix: device.code_prefix, counter, at: when }) }
}

export interface RollToConfirm {
  roll: Omit<ConfirmedRoll, 'command_id' | 'confirmed_at'>
  /** The actor who scanned it: the operator, or the supervisor assigning it on the spot. */
  actorId: string
}

export interface OpenDayInput {
  actorId: string
  session: Omit<
    GateSession,
    'id' | 'code' | 'open_command_id' | 'rolls' | 'status' | 'closed_at' | 'close_command_id'
  >
  rolls: RollToConfirm[]
  rosterVersion: string | null
  receiptBookId?: string | null
}

/** F4.1 / F4-B3.b: open_session then confirm_roll for the roll and strip, atomically and in order. */
export async function openDay(input: OpenDayInput): Promise<GateSession> {
  const sessionId = newId()
  const openedAt = input.session.opened_at
  const code = await mintSessionCode(Date.parse(openedAt))
  const gps = input.session.capture_mode === 'APP' ? await currentGps() : null
  const skew = await clockSkewSeconds()
  const tag = input.rolls.find((r) => r.roll.kind === 'BAG')
  const seal = input.rolls.find((r) => r.roll.kind === 'SEAL')
  const open: OpenSessionPayload = {
    code,
    group_id: input.session.group_id,
    capture_mode: input.session.capture_mode,
    operator_id: input.session.operator_id,
    opened_at: openedAt,
    gps,
    scale_equipment_id: input.session.scale_equipment_id,
    stamp_ok: input.session.stamp_ok,
    test_weight_g: input.session.test_weight_g,
    test_weight_read_g: input.session.test_weight_read_g,
    tag_batch_id: tag?.roll.batch_id ?? null,
    seal_batch_id: seal?.roll.batch_id ?? null,
    roster_version: input.rosterVersion,
    clock_skew_s: skew,
    tags_in_hand: input.session.tags_in_hand,
    seals_in_hand: input.session.seals_in_hand,
    receipt_book_id: input.receiptBookId ?? null,
  }
  const openId = newId()
  const captureMode = input.session.capture_mode === 'PAPER' ? 'PAPER_BACKFILL' : 'APP'
  const occurredAt = new Date(openedAt)
  const commands: EnqueueInput[] = [
    {
      id: openId,
      type: 'open_session',
      payload: open,
      actorId: input.actorId,
      sessionId,
      captureMode,
      occurredAt,
    },
  ]
  const rolls: ConfirmedRoll[] = []
  for (const r of input.rolls) {
    const id = newId()
    const payload: ConfirmRollPayload = {
      batch_id: r.roll.batch_id,
      kind: r.roll.kind,
      first_payload: r.roll.first_payload,
      last_payload: r.roll.last_payload,
      spare: r.roll.spare,
      tags_in_roll: r.roll.count,
    }
    commands.push({
      id,
      type: 'confirm_roll',
      payload,
      actorId: r.actorId,
      sessionId,
      captureMode,
      parentIds: [openId],
      occurredAt,
    })
    rolls.push({ ...r.roll, command_id: id, confirmed_at: openedAt })
  }
  await enqueueCommands(commands)
  const session: GateSession = {
    ...input.session,
    id: sessionId,
    code,
    status: 'OPEN',
    closed_at: null,
    close_command_id: null,
    open_command_id: openId,
    rolls,
  }
  await saveSession(session)
  return session
}

/** A spare roll or strip opened during the day (F4.1 if_wrong), or a supervisor's on-the-spot assignment. */
export async function confirmSpareRoll(session: GateSession, r: RollToConfirm): Promise<void> {
  const id = newId()
  const now = new Date().toISOString()
  const payload: ConfirmRollPayload = {
    batch_id: r.roll.batch_id,
    kind: r.roll.kind,
    first_payload: r.roll.first_payload,
    last_payload: r.roll.last_payload,
    spare: r.roll.spare,
    tags_in_roll: r.roll.count,
  }
  await enqueueCommand({
    id,
    type: 'confirm_roll',
    payload,
    actorId: r.actorId,
    sessionId: session.id,
    captureMode: session.capture_mode === 'PAPER' ? 'PAPER_BACKFILL' : 'APP',
    parentIds: [session.open_command_id],
  })
  const fresh = (await getSession(session.id)) ?? session
  await saveSession({
    ...fresh,
    rolls: [...fresh.rolls, { ...r.roll, command_id: id, confirmed_at: now }],
  })
}

// ---------------------------------------------------------------------------------------------
// Deliveries
// ---------------------------------------------------------------------------------------------

export async function saveDeliveryDraft(d: DeliveryDraft, actorId: string | null): Promise<void> {
  await saveDraft<DeliveryDraft>({
    id: d.delivery_ulid,
    kind: DRAFT_KIND,
    actor_id: actorId,
    session_id: d.session_id,
    data: d,
  })
  // Back it up to the server shortly (beside the purchase: never awaited, never throws).
  scheduleDraftBackup()
}

function mediaOfDraft(d: DeliveryDraft): string[] {
  const ids = [
    d.delivered_by?.kind === 'OTHER' ? d.delivered_by.photo_media_id : null,
    ...d.failed_bags.map((f) => f.photo_media_id),
    d.sample?.photo_media_id ?? null,
    d.slip.photo_media_id,
    d.paper?.page_photo_media_id ?? null,
    ...d.disputes.flatMap((x) => x.photo_ids),
    d.identification.new_photo_media_id ?? null,
    ...d.bags.flatMap((b) => b.weights.map((w) => w.photo_media_id ?? null)),
    ...d.moisture.readings.map((r) => r.photo_media_id ?? null),
  ]
  return [...new Set(ids.filter((x): x is string => !!x))]
}

export async function commitDelivery(
  d: DeliveryDraft,
  session: GateSession,
  actorId: string,
  ctx: { cards: Partial<Record<'MAIN' | 'CONV', CardRef>>; season_light: SeasonLight },
): Promise<string> {
  const closedAt = d.paper ? d.paper.slip_time : new Date().toISOString()
  const payload = buildCommitPayload(d, { ...ctx, closed_at: closedAt })
  const rec = await enqueueFromDraft(
    {
      type: 'commit_delivery',
      payload,
      actorId,
      sessionId: session.id,
      captureMode: d.capture_mode,
      parentIds: [session.open_command_id],
      mediaIds: mediaOfDraft(d),
      occurredAt: new Date(closedAt),
    },
    d.delivery_ulid,
  )
  return rec.id
}

export async function voidDelivery(
  d: DeliveryDraft,
  session: GateSession,
  actorId: string,
): Promise<string> {
  const at = new Date().toISOString()
  const payload = buildVoidPayload(d, at)
  const rec = await enqueueFromDraft(
    {
      type: 'void_delivery',
      payload,
      actorId,
      sessionId: session.id,
      captureMode: d.capture_mode,
      parentIds: [session.open_command_id],
      mediaIds: mediaOfDraft(d),
    },
    d.delivery_ulid,
  )
  return rec.id
}

/**
 * Delete an open farmer from Home's list: the delivery is voided with its reason (lot codes, tags
 * and seal written VOID, never reused) and the draft leaves the phone with the command.
 */
export async function deleteOpenDelivery(
  d: DeliveryDraft,
  reason: VoidDeliveryReason,
  session: GateSession,
  actorId: string,
): Promise<string> {
  const next = deliveryReducer(d, {
    type: 'start_cancel',
    reason,
    reason_code: null,
    at: new Date().toISOString(),
  })
  return voidDelivery(next, session, actorId)
}

/** A tag or seal spoiled outside any delivery (F4.6 void_tag). */
export async function voidLooseTag(
  session: GateSession,
  actorId: string,
  payload: VoidTagPayload,
): Promise<void> {
  await enqueueCommand({
    type: 'void_tag',
    payload,
    actorId,
    sessionId: session.id,
    captureMode: session.capture_mode === 'PAPER' ? 'PAPER_BACKFILL' : 'APP',
    parentIds: [session.open_command_id],
  })
}

export async function retag(
  session: GateSession | null,
  supervisorId: string,
  payload: RetagPayload,
): Promise<void> {
  await enqueueCommand({
    type: 'retag',
    payload,
    actorId: supervisorId,
    sessionId: session?.id ?? null,
    parentIds: session ? [session.open_command_id] : [],
  })
}

export async function spoilSlip(
  session: GateSession,
  actorId: string,
  slip: { book_id: string; slip_no: string; photo_id: string },
): Promise<void> {
  const payload: SpoilSlipPayload = {
    receipt_slip: { id: newId(), ...slip },
    session_id: session.id,
    occurred_at: new Date().toISOString(),
  }
  await enqueueCommand({
    type: 'spoil_slip',
    payload,
    actorId,
    sessionId: session.id,
    captureMode: 'PAPER_BACKFILL',
    parentIds: [session.open_command_id],
    mediaIds: [slip.photo_id],
  })
}

/** F4.11 / F4-B3.b: close_session; the server reconciles once every listed command arrived. */
export async function closeDay(
  session: GateSession,
  actorId: string,
  p: Omit<CloseSessionPayload, 'closed_at' | 'command_count'> & { closed_at?: string },
): Promise<void> {
  const db = getDb()
  const commandCount = await db.outbox.where('session_id').equals(session.id).count()
  const closedAt = p.closed_at ?? new Date().toISOString()
  const payload: CloseSessionPayload = { ...p, closed_at: closedAt, command_count: commandCount }
  const rec = await enqueueCommand({
    type: 'close_session',
    payload,
    actorId,
    sessionId: session.id,
    captureMode: session.capture_mode === 'PAPER' ? 'PAPER_BACKFILL' : 'APP',
    parentIds: [session.open_command_id],
    occurredAt: new Date(closedAt),
  })
  const fresh = (await getSession(session.id)) ?? session
  await saveSession({
    ...fresh,
    status: 'CLOSED',
    closed_at: closedAt,
    close_command_id: rec.id,
    paper: fresh.paper ? { ...fresh.paper, day_closed_at: closedAt } : null,
  })
}

/**
 * R10 / F4.8: once a delivery has reached the server, the phone keeps its lot code, bags, kg and
 * payment status and drops the money figures. Synced commit_delivery payloads lose their
 * phone_figures.amount_paise (they are never resent once done).
 */
export async function scrubSyncedMoney(db = getDb()): Promise<number> {
  type Money = { lots?: Array<{ phone_figures?: { amount_paise?: number | null } }> }
  const done = await db.outbox
    .where('type')
    .equals('commit_delivery')
    .and((r) => ['APPLIED', 'DUPLICATE', 'HELD'].includes(r.status))
    .toArray()
  // Payloads may be sealed (R10): open, scrub and re-seal outside the transaction (WebCrypto).
  const opened = await openOutboxRecords(done, db)
  const writes: Array<{ seq: number; payload: unknown }> = []
  for (const r of opened) {
    if (r.payload_locked || r.seq === undefined) continue
    const p = r.payload as Money
    const dirty = (p.lots ?? []).some(
      (l) => l.phone_figures && l.phone_figures.amount_paise !== null,
    )
    if (!dirty) continue
    const clean: Money = {
      ...p,
      lots: (p.lots ?? []).map((l) =>
        l.phone_figures ? { ...l, phone_figures: { ...l.phone_figures, amount_paise: null } } : l,
      ),
    }
    writes.push({ seq: r.seq, payload: await sealValue(clean, db) })
  }
  if (writes.length === 0) return 0
  await db.transaction('rw', db.outbox, async () => {
    for (const w of writes) await db.outbox.update(w.seq, { payload: w.payload })
  })
  return writes.length
}

/**
 * V1: no internet at the gate — keep the farmer's account number + IFSC in the outbox (sealed
 * with the device key) and let the server run OnGrid verification on sync. Not tied to a day.
 */
export async function queueBankAccount(
  actorId: string,
  payload: AddBankAccountPayload,
  db = getDb(),
): Promise<string> {
  const rec = await enqueueCommand({ type: 'add_bank_account', payload, actorId }, db)
  return rec.id
}

/**
 * Forget the full account number once the server has answered (applied, held, or refused for
 * good): the record is deleted, not kept the 30 days other finished commands are.
 */
export async function scrubSyncedBank(db = getDb()): Promise<number> {
  return db.outbox
    .where('type')
    .equals('add_bank_account')
    .and((r) => ['APPLIED', 'DUPLICATE', 'HELD', 'ERROR', 'SET_ASIDE'].includes(r.status))
    .delete()
}
